import type { User } from '@supabase/supabase-js'
import { supabase } from '@/shared/supabase/client'
import { AppError, toAppError } from '@/shared/lib/appError'
import type { AuthUser } from '@/shared/api/authApi'
import {
  buildSupabaseOAuthRedirectTo,
  mapOAuthCallbackErrorToMessage,
  readOAuthCallbackParams,
} from '@/shared/lib/googleOAuthLogin'

const isBrowser = typeof window !== 'undefined'

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined

const readGoogleDisplayName = (metadata: Record<string, unknown>): string | undefined =>
  readString(metadata.display_name) ??
  readString(metadata.name) ??
  readString(metadata.full_name)

/**
 * Alinha metadados pós-primeiro OAuth ao que o app precisa antes de /select-profile.
 * Não grava `role`: a escolha médico/paciente fica em `completeOnboarding` (como na prática do onboarding).
 * Nome: espelha `signUp` (`display_name` / `name`); `onboarding_completed: false` até concluir o perfil.
 */
export const syncGoogleOAuthUserMetadataIfNeeded = async (user: User): Promise<User> => {
  const metadata = asRecord(user.user_metadata)
  const updates: Record<string, unknown> = {}

  const displayName = readGoogleDisplayName(metadata)
  if (displayName && !readString(metadata.display_name)) {
    updates.display_name = displayName
    updates.name = displayName
  }

  if (metadata.onboarding_completed !== true && metadata.onboarding_completed !== false) {
    updates.onboarding_completed = false
  }

  if (!Object.keys(updates).length) {
    return user
  }

  const { data, error } = await supabase.auth.updateUser({ data: updates })
  if (error) {
    throw toAppError(error, 'Não foi possível sincronizar o perfil do Google.')
  }

  return data.user ?? user
}

type FinishOAuthDeps = {
  requireCurrentUser: (fallbackMessage?: string) => Promise<User>
  refreshLegacyTokenFromCurrentSession: () => Promise<void>
  formatUser: (user: User) => AuthUser
}

export const signInWithGoogleOAuth = async (): Promise<void> => {
  const redirectTo = buildSupabaseOAuthRedirectTo({
    origin: isBrowser ? window.location.origin : undefined,
    siteUrlEnv: isBrowser
      ? undefined
      : (import.meta.env.VITE_AUTH_SITE_URL as string | undefined),
  })

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo },
  })

  if (error) {
    throw new AppError(mapOAuthCallbackErrorToMessage(error.message, error.message), {
      status: error.status ?? 400,
      cause: error,
    })
  }

  if (data?.url && isBrowser) {
    window.location.assign(data.url)
  }
}

export const finishOAuthCallback = async (
  searchParams: URLSearchParams,
  deps: FinishOAuthDeps,
): Promise<{ user: AuthUser }> => {
  const { code, error, errorDescription } = readOAuthCallbackParams(searchParams)
  if (error) {
    throw new AppError(mapOAuthCallbackErrorToMessage(error, errorDescription), {
      status: 400,
      code: 'UNAUTHORIZED',
    })
  }

  if (code) {
    const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
    if (exchangeError) {
      throw new AppError(
        mapOAuthCallbackErrorToMessage(exchangeError.message, exchangeError.message),
        { status: exchangeError.status ?? 400, cause: exchangeError },
      )
    }
  } else {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
    if (sessionError) {
      throw toAppError(sessionError, 'Não foi possível concluir o login com Google.', 400)
    }
    if (!sessionData.session?.user) {
      throw new AppError('Sessão não encontrada após o retorno do Google.', {
        status: 400,
        code: 'UNAUTHORIZED',
      })
    }
  }

  const rawUser = await deps.requireCurrentUser('Não foi possível carregar a sessão do Google.')
  const syncedUser = await syncGoogleOAuthUserMetadataIfNeeded(rawUser)
  await deps.refreshLegacyTokenFromCurrentSession()
  return { user: deps.formatUser(syncedUser) }
}
